import { Card } from "@/components/ui/Card"
import { formatMoneyFull } from "@/lib/money"
import type { MarginFigures, Margins } from "./types"

const BLANK = "—"

function percent(margin: number | null | undefined): string {
  return margin === null || margin === undefined ? BLANK : `${(margin * 100).toFixed(1)}%`
}

// One column per side: every other channel, then wholesale when the market
// has a wholesale channel.
function columns(margins: Margins): [string, MarginFigures | null][] {
  const out: [string, MarginFigures | null][] = [["Other channels", margins.other]]
  if (margins.wholesale !== undefined) out.push(["Wholesale", margins.wholesale])
  return out
}

// Actual ASP, COGS and margin from the last 90 days' sales, gifts left out.
// COGS and margin rows show only when the server sent them (the viewer may
// see gross margin).
export default function MarginsCard({ margins, currency }: { margins: Margins; currency: string }) {
  const cols = columns(margins)
  const sent = cols.find(([, f]) => f)?.[1]
  const showCogs = sent?.cogs !== undefined
  const rows: [string, (f: MarginFigures) => string][] = [["ASP", (f) => formatMoneyFull(f.asp, currency)]]
  if (showCogs) {
    rows.push(["COGS", (f) => (f.cogs === undefined ? BLANK : formatMoneyFull(f.cogs, currency))])
    rows.push(["Margin", (f) => percent(f.margin)])
  }

  return (
    <div className="px-5 pt-4">
      <Card className="flex flex-col gap-2 p-3">
        <div className="text-xs font-medium text-foreground">Last 90 days · gifts not counted</div>
        {!sent ? (
          <p className="text-sm text-foreground">No sales in the last 90 days.</p>
        ) : (
          <div
            className="grid items-baseline gap-x-4 gap-y-1 text-sm text-foreground"
            style={{ gridTemplateColumns: `auto repeat(${cols.length}, minmax(0, 1fr))` }}
          >
            <span />
            {cols.map(([name]) => (
              <span key={name} className="text-right text-xs font-medium">{name}</span>
            ))}
            {rows.map(([label, value]) => (
              <Row key={label} label={label} cols={cols} value={value} />
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function Row({ label, cols, value }: {
  label: string
  cols: [string, MarginFigures | null][]
  value: (f: MarginFigures) => string
}) {
  return (
    <>
      <span>{label}</span>
      {cols.map(([name, f]) => (
        <span key={name} className="text-right text-base font-semibold tabular-nums">{f ? value(f) : BLANK}</span>
      ))}
    </>
  )
}
